import React, { useRef, useState, useEffect } from 'react';
import { styles } from '../../../utils/styles';
import { CollapsibleSectionHeader } from './components/CollapsibleSectionHeader';
import { DropdownIcon } from './components/DropdownIcon';
import { RemoveIcon } from './components/RemoveIcon';
import { useIconUpload } from '../../../hooks/useIconUpload';
import { uiModelApiService } from '../../../services/UIModelApiService';
import { useUIModelApi } from '../../../contexts/UIModelApiContext';

// Image validation constants
const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // 2MB in bytes
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

type ProductDetailsProps = {
    id?: string;
    isExpanded: boolean;
    onToggleExpanded: () => void;

    // Product Details state
    showModelNumber: boolean;
    showProductDescription: boolean;
    descriptionStyle: string;
    showPrice: boolean;
    showAvailabilityStatus: boolean;
    inStockLabel: string;
    backOrderLabel: string;
    showVariants: boolean;
    showNumberOfVariants: boolean;
    showCategory: boolean;
    showManufacturer: boolean;
    productDetailsTextAlignment: 'Left' | 'Centre' | 'Right';
    showButtonText: boolean;
    buttonLabel: string;
    showButtonIcon: boolean;
    buttonPlacement: 'Left' | 'Centre' | 'Right';
    buttonIconUrl?: string;

    // Event handlers
    onConfigChange: (key: string, value: any) => void;
}

export const ProductDetails = ({
    isExpanded,
    onToggleExpanded,
    showModelNumber,
    showProductDescription,
    descriptionStyle,
    showPrice,
    showAvailabilityStatus,
    inStockLabel,
    backOrderLabel,
    showVariants,
    showNumberOfVariants,
    showCategory,
    showManufacturer,
    productDetailsTextAlignment,
    showButtonText,
    buttonLabel,
    showButtonIcon,
    buttonPlacement,
    buttonIconUrl,
    onConfigChange
}: ProductDetailsProps) => {
    const inStockLabelDebounceRef = useRef<NodeJS.Timeout>();
    const backOrderLabelDebounceRef = useRef<NodeJS.Timeout>();
    const buttonLabelDebounceRef = useRef<NodeJS.Timeout>();

    const [localInStockLabel, setLocalInStockLabel] = useState(inStockLabel);
    const [localBackOrderLabel, setLocalBackOrderLabel] = useState(backOrderLabel);
    const [localButtonLabel, setLocalButtonLabel] = useState(buttonLabel);
    const [iconValidationError, setIconValidationError] = useState<string>('');
    const [isUploadingIcon, setIsUploadingIcon] = useState(false);
    const errorDismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setLocalInStockLabel(inStockLabel);
    }, [inStockLabel]);

    useEffect(() => {
        setLocalBackOrderLabel(backOrderLabel);
    }, [backOrderLabel]);

    useEffect(() => {
        setLocalButtonLabel(buttonLabel);
    }, [buttonLabel]);

    useEffect(() => {
        return () => {
            if (inStockLabelDebounceRef.current) {
                clearTimeout(inStockLabelDebounceRef.current);
            }
            if (backOrderLabelDebounceRef.current) {
                clearTimeout(backOrderLabelDebounceRef.current);
            }
            if (buttonLabelDebounceRef.current) {
                clearTimeout(buttonLabelDebounceRef.current);
            }
            if (errorDismissTimeoutRef.current) {
                clearTimeout(errorDismissTimeoutRef.current);
            }
        };
    }, []);

    const showErrorMessage = (message: string) => {
        setIconValidationError(message);

        // Clear any existing timeout
        if (errorDismissTimeoutRef.current) {
            clearTimeout(errorDismissTimeoutRef.current);
        }

        // Auto-dismiss after 2.5 seconds
        errorDismissTimeoutRef.current = setTimeout(() => {
            setIconValidationError('');
        }, 2500);
    };

    const { configureId, currentUIModelId } = useUIModelApi();

    const validateImageFile = (file: File): { isValid: boolean; error: string } => {
        // Check file size
        if (file.size > MAX_FILE_SIZE_BYTES) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            return {
                isValid: false,
                error: `File size (${fileSizeMB}MB) exceeds maximum limit of ${MAX_FILE_SIZE_MB}MB`
            };
        }

        // Check file type
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            return {
                isValid: false,
                error: 'File type not supported. Please upload JPEG, PNG, GIF, or WebP'
            };
        }

        return { isValid: true, error: '' };
    };

    const handleIconRemove = (url: string) => {
        if (url === '') {
            onConfigChange('buttonIconUrl', '');
            setIconValidationError('');
        }
    };

    const buttonIconUpload = useIconUpload(buttonIconUrl || '', handleIconRemove);

    const handleButtonIconUpload = async (file: File | null) => {
        if (!file) return;

        // Clear previous error
        setIconValidationError('');

        // Validate file before upload
        const validation = validateImageFile(file);
        if (!validation.isValid) {
            showErrorMessage(validation.error);
            // Reset the file input
            if (buttonIconUpload.inputRef.current) {
                buttonIconUpload.inputRef.current.value = '';
            }
            return;
        }

        if (!configureId || !currentUIModelId) {
            return;
        }

        try {
            setIsUploadingIcon(true);
            const response = await uiModelApiService.uploadImageIcon(
                configureId,
                currentUIModelId,
                file,
                'buttonIcon'
            );

            if (response.success && response.data.imageUrl) {
                onConfigChange('buttonIconUrl', response.data.imageUrl);
                setIconValidationError('');
            } else {
                showErrorMessage('Upload failed. Please try again.');
            }

        } catch (error) {
            console.error('Upload failed:', error);
            showErrorMessage('Upload failed. Please try again.');
        } finally {
            setIsUploadingIcon(false);
        }
    };

    const handleDebouncedInStockLabelChange = (value: string) => {
        setLocalInStockLabel(value);
        if (inStockLabelDebounceRef.current) {
            clearTimeout(inStockLabelDebounceRef.current);
        }
        inStockLabelDebounceRef.current = setTimeout(() => {
            onConfigChange('inStockLabel', value);
        }, 500);
    };

    const handleDebouncedBackOrderLabelChange = (value: string) => {
        setLocalBackOrderLabel(value);
        if (backOrderLabelDebounceRef.current) {
            clearTimeout(backOrderLabelDebounceRef.current);
        }
        backOrderLabelDebounceRef.current = setTimeout(() => {
            onConfigChange('backOrderLabel', value);
        }, 500);
    };

    const handleDebouncedButtonLabelChange = (value: string) => {
        setLocalButtonLabel(value);
        if (buttonLabelDebounceRef.current) {
            clearTimeout(buttonLabelDebounceRef.current);
        }
        buttonLabelDebounceRef.current = setTimeout(() => {
            onConfigChange('buttonLabel', value);
        }, 500);
    };

    return (
        <div className="tw-border-b tw-border-[#D1D1D6]">
            <CollapsibleSectionHeader
                title="Product Details"
                isExpanded={isExpanded}
                onToggle={onToggleExpanded}
            />

            {/* Expanded Product Details Content */}
            {isExpanded && (
                <div className="tw-px-4 tw-pb-4">
                    {/* Show Model Number */}
                    <div className="tw-mb-3 tw-flex tw-items-center">
                        <span className="tw-mr-2 tw-text-gray-400">::</span>
                        <label className="tw-flex tw-items-center tw-flex-1">
                            <input
                                type="checkbox"
                                checked={showModelNumber}
                                onChange={(e) => onConfigChange('showModelNumber', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <span className="tw-text-[#6D788C] tw-text-sm">Show Model Number</span>
                        </label>
                    </div>
                    
                    {/* Show Product Description */}
                    <div className="tw-mb-3 tw-flex tw-items-center">
                        <span className="tw-mr-2 tw-text-gray-400">::</span>
                        <label className="tw-flex tw-items-center tw-flex-1">
                            <input
                                type="checkbox"
                                checked={showProductDescription}
                                onChange={(e) => onConfigChange('showProductDescription', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <span className="tw-text-[#6D788C] tw-text-sm">Show Product Description</span>
                        </label>
                    </div>
                    
                    {/* Description Style (only if description is shown) */}
                    {showProductDescription && (
                        <div className="tw-mb-4 tw-ml-8">
                            <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                Description Style
                            </label>
                            <div className={styles.selectContainer}>
                                <select
                                    value={descriptionStyle}
                                    onChange={(e) => onConfigChange('descriptionStyle', e.target.value)}
                                    className={styles.select}
                                >
                                    <option value="Partly">Partly</option>
                                    <option value="Full">Full</option>
                                    <option value="Collapsed">Collapsed</option>
                                </select>
                                <div className={styles.selectIcon}>
                                    <DropdownIcon />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Show Price */}
                    <div className="tw-mb-3 tw-flex tw-items-center">
                        <span className="tw-mr-2 tw-text-gray-400">::</span>
                        <label className="tw-flex tw-items-center tw-flex-1">
                            <input
                                type="checkbox"
                                checked={showPrice}
                                onChange={(e) => onConfigChange('showPrice', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <span className="tw-text-[#6D788C] tw-text-sm">Show Price</span>
                        </label>
                    </div>
                    
                    {/* Show Availability Status */}
                    <div className="tw-mb-3 tw-flex tw-items-center">
                        <span className="tw-mr-2 tw-text-gray-400">::</span>
                        <label className="tw-flex tw-items-center tw-flex-1">
                            <input
                                type="checkbox"
                                checked={showAvailabilityStatus}
                                onChange={(e) => onConfigChange('showAvailabilityStatus', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <span className="tw-text-[#6D788C] tw-text-sm">Show Availability Status</span>
                        </label>
                    </div>
                    
                    {/* Availability Status Labels (only if status is shown) */}
                    {showAvailabilityStatus && (
                        <div className="tw-mb-4 tw-ml-8">
                            <div className="tw-mb-3">
                                <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                    In Stock Label
                                </label>
                                <input
                                    type="text"
                                    value={localInStockLabel}
                                    onChange={(e) => handleDebouncedInStockLabelChange(e.target.value)}
                                    className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
                                />
                            </div>
                            
                            <div className="tw-mb-3">
                                <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                    Back Order Label
                                </label>
                                <input
                                    type="text"
                                    value={localBackOrderLabel}
                                    onChange={(e) => handleDebouncedBackOrderLabelChange(e.target.value)}
                                    className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Show Category */}
                    <div className="tw-mb-3 tw-flex tw-items-center">
                        <span className="tw-mr-2 tw-text-gray-400">::</span>
                        <label className="tw-flex tw-items-center tw-flex-1">
                            <input
                                type="checkbox"
                                checked={showCategory}
                                onChange={(e) => onConfigChange('showCategory', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <span className="tw-text-[#6D788C] tw-text-sm">Show Category</span>
                        </label>
                    </div>
                    
                    {/* Show Manufacturer */}
                    <div className="tw-mb-3 tw-flex tw-items-center">
                        <span className="tw-mr-2 tw-text-gray-400">::</span>
                        <label className="tw-flex tw-items-center tw-flex-1">
                            <input
                                type="checkbox"
                                checked={showManufacturer}
                                onChange={(e) => onConfigChange('showManufacturer', e.target.checked)}
                                className={styles.checkboxInput}
                            />
                            <span className="tw-text-[#6D788C] tw-text-sm">Show Manufacturer</span>
                        </label>
                    </div>
                    
                    {/* Button Section */}
                    <div className="tw-mt-6">
                        <h4 className="tw-text-[#6D788C] tw-text-sm tw-font-semibold tw-mb-3">Button</h4>
                        
                        {/* Show Text */}
                        <div className="tw-mb-4">
                            <label className="tw-flex tw-items-center">
                                <input
                                    type="checkbox"
                                    checked={showButtonText}
                                    onChange={(e) => onConfigChange('showButtonText', e.target.checked)}
                                    className={styles.checkboxInput}
                                />
                                <span className={styles.checkboxLabel}>Show Text</span>
                            </label>
                            
                            {showButtonText && (
                                <div className="tw-mt-3 tw-ml-6">
                                    <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                                        Button Label
                                    </label>
                                    <input
                                        type="text"
                                        value={localButtonLabel}
                                        onChange={(e) => handleDebouncedButtonLabelChange(e.target.value)}
                                        className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
                                    />
                                </div>
                            )}
                        </div>
                        
                        {/* Show Icon */}
                        <div className="tw-mb-4 tw-ml-6">
                            <label className="tw-flex tw-items-center">
                                <input
                                    type="checkbox"
                                    checked={showButtonIcon}
                                    onChange={(e) => onConfigChange('showButtonIcon', e.target.checked)}
                                    className={styles.checkboxInput}
                                />
                                <span className="tw-text-[#6D788C] tw-text-sm">Show Icon</span>
                            </label>
                        </div>

                        {/* Button Icon Upload */}
                        {showButtonIcon && (
                            <div className={styles.nestedInputContainerWithMargin}>
                                <label className={styles.inputLabel}>Button Icon</label>

                                {buttonIconUpload.previewUrl && (
                                    <div className="tw-mb-2">
                                        <div className="tw-relative tw-inline-block tw-w-[50px] tw-h-[50px]">
                                            <img
                                                src={buttonIconUpload.previewUrl}
                                                alt="Button icon preview"
                                                className="tw-w-full tw-h-full tw-border tw-border-gray-300 tw-rounded tw-p-1 tw-object-contain"
                                            />
                                            <button
                                                onClick={buttonIconUpload.handleRemove}
                                                className="tw-absolute tw-top-0 tw-right-0 tw-bg-red-500 tw-text-white tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600"
                                                title="Remove icon"
                                                type="button"
                                            >
                                                <RemoveIcon />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Validation Error Message */}
                                {iconValidationError && (
                                    <div className="tw-mb-2 tw-p-2 tw-bg-red-100 tw-border tw-border-red-300 tw-rounded tw-text-red-700 tw-text-xs">
                                        {iconValidationError}
                                    </div>
                                )}

                                <input
                                    ref={buttonIconUpload.inputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        buttonIconUpload.handleUpload(e);
                                        if (e.target.files?.[0]) {
                                            handleButtonIconUpload(e.target.files[0]);
                                        }
                                    }}
                                    className="tw-hidden"
                                    id="button-icon-upload-input"
                                    disabled={isUploadingIcon}
                                />

                                <button
                                    className={styles.uploadButton}
                                    onClick={() => buttonIconUpload.inputRef.current?.click()}
                                    type="button"
                                    disabled={isUploadingIcon}
                                >
                                    {isUploadingIcon ? 'Uploading...' : (buttonIconUpload.previewUrl ? 'Change Icon' : 'Upload Icon')}
                                </button>

                                {buttonIconUpload.file && (
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                        Selected: {buttonIconUpload.file.name}
                                    </p>
                                )}

                                {/* File size info */}
                                <p className="tw-text-xs tw-text-gray-400 tw-mt-2">
                                    Maximum file size: {MAX_FILE_SIZE_MB}MB. Supported formats: JPEG, PNG, GIF, WebP
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};